// store/patientFeedbackSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Types for patient feedback
export type UrgencyLevel = 'standard' | 'urgent' | 'emergency';
export type VisitType = 'emergency' | 'outpatient' | 'inpatient' | 'virtual' | '';
export type VisitLocation = 'er' | 'opd' | 'ward' | 'other' | '';
export type FeedbackTag = 'nursing' | 'medical' | 'financial' | 'facilities' | 'safety' | 'service' | 'other';

export interface PatientFeedback {
  id?: number;
  tracking_number?: string;
  content: string;
  submission_method: 'web_form' | 'voice_recording' | 'structured_survey' | 'family_submission' | 'file_upload';
  original_language: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  room_number?: string;
  status: 'submitted' | 'awaiting_review' | 'analyzing' | 'routed' | 'in_progress' | 'resolved' | 'closed';
  status_display?: string;
  submitted_at?: string;
  first_response_at?: string;
  resolved_at?: string;
  satisfaction_rating?: number;
  satisfaction_comment?: string;
  is_anonymous: boolean;
  consent_given: boolean;
  is_visit_related: boolean;
  visit_type?: VisitType;
  visit_location?: VisitLocation;
  urgency_level: UrgencyLevel;
  feedback_tags: FeedbackTag[];
  is_emergency?: boolean;
  requires_immediate_attention?: boolean;
  ai_analysis?: {
    severity_level: 'critical' | 'urgent' | 'routine' | 'informational';
    severity_display: string;
    primary_domain: string;
    domain_display: string;
    sentiment_score: number;
    confidence_score: number;
    key_issues: string[];
    recommended_actions: string[];
  };
  responses?: StaffResponse[];
  files?: FeedbackFile[];
}

export interface StaffResponse {
  id: number;
  staff_member: {
    id: number;
    first_name: string;
    last_name: string;
    role_display: string;
  };
  response_content: string;
  response_type: 'acknowledgment' | 'status_update' | 'resolution' | 'escalation' | 'follow_up';
  response_type_display: string;
  sent_at: string;
  is_ai_generated: boolean;
  visible_to_patient: boolean;
}

export interface FeedbackFile {
  id: number;
  original_filename: string;
  file_type: 'image' | 'document' | 'audio';
  file_size: number;
  file_size_display: string;
  virus_scan_status: 'pending' | 'clean' | 'infected' | 'error';
  uploaded_at: string;
}

export interface FeedbackSubmissionData {
  content: string;
  feedback_category?: string;
  submission_method: PatientFeedback['submission_method'];
  original_language: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  room_number?: string;
  is_anonymous: boolean;
  consent_given: boolean;
  is_visit_related: boolean;
  visit_type?: VisitType;
  visit_location?: VisitLocation;
  urgency_level: UrgencyLevel;
  feedback_tags: FeedbackTag[];
  hospital_code?: string;
}

export interface FileUploadData {
  file: File;
  feedback_id?: number;
}

export interface SatisfactionRating {
  rating: number;
  comment?: string;
}

// API response types
export interface SubmissionResponse {
  id: number;
  tracking_number: string;
  status: string;
  submitted_at: string;
  message: string;
}

// Async thunks
export const submitFeedback = createAsyncThunk(
  'patientFeedback/submit',
  async (data: FeedbackSubmissionData, { rejectWithValue }) => {
    try {
      const { patientFeedbackApi } = await import('../services/patientApi');
      return await patientFeedbackApi.submitFeedback(data);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const uploadFile = createAsyncThunk(
  'patientFeedback/uploadFile',
  async (data: FileUploadData, { rejectWithValue }) => {
    try {
      const { patientFeedbackApi } = await import('../services/patientApi');
      return await patientFeedbackApi.uploadFile(data.file, data.feedback_id);
    } catch (error: any) {
      return rejectWithValue(error.message || 'File upload failed');
    }
  }
);

export const trackFeedback = createAsyncThunk(
  'patientFeedback/track',
  async (feedbackId: number, { rejectWithValue }) => {
    try {
      const { patientFeedbackApi } = await import('../services/patientApi');
      return await patientFeedbackApi.trackFeedback(feedbackId);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to track feedback');
    }
  }
);

export const rateSatisfaction = createAsyncThunk(
  'patientFeedback/rate',
  async ({ feedbackId, rating }: { feedbackId: number; rating: SatisfactionRating }, { rejectWithValue }) => {
    try {
      const { patientFeedbackApi } = await import('../services/patientApi');
      return await patientFeedbackApi.rateSatisfaction(feedbackId, rating);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to submit rating');
    }
  }
);

export const fetchUserFeedback = createAsyncThunk(
  'patientFeedback/fetchUserFeedback',
  async (_, { rejectWithValue }) => {
    try {
      const { patientFeedbackApi } = await import('../services/patientApi');
      return await patientFeedbackApi.getFeedbackList();
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch feedback');
    }
  }
);

// Initial state
interface PatientFeedbackState {
  // Current submission
  currentSubmission: Partial<FeedbackSubmissionData> | null;
  submissionStep: 'content' | 'contact' | 'privacy' | 'review' | 'submitted';
  
  // Submitted feedback
  submittedFeedback: SubmissionResponse[];
  trackedFeedback: { [id: number]: PatientFeedback };
  
  // UI state
  isSubmitting: boolean;
  isTracking: boolean;
  isUploadingFile: boolean;
  isRatingSubmitted: boolean;
  
  // Form state
  selectedFeedbackType: string;
  attachedFiles: File[];
  voiceRecording: Blob | null;
  
  // Errors and messages
  submissionError: string | null;
  trackingError: string | null;
  uploadError: string | null;
  successMessage: string | null;
  
  // Analytics
  feedbackHistory: PatientFeedback[];
  totalSubmissions: number;
  pendingFeedback: number;
}

const initialState: PatientFeedbackState = {
  currentSubmission: null,
  submissionStep: 'content',
  submittedFeedback: [],
  trackedFeedback: {},
  isSubmitting: false,
  isTracking: false,
  isUploadingFile: false,
  isRatingSubmitted: false,
  selectedFeedbackType: 'general',
  attachedFiles: [],
  voiceRecording: null,
  submissionError: null,
  trackingError: null,
  uploadError: null,
  successMessage: null,
  feedbackHistory: [],
  totalSubmissions: 0,
  pendingFeedback: 0,
};

const patientFeedbackSlice = createSlice({
  name: 'patientFeedback',
  initialState,
  reducers: {
    // Form management
    updateSubmission: (state, action: PayloadAction<Partial<FeedbackSubmissionData>>) => {
      state.currentSubmission = {
        ...(state.currentSubmission || {}),
        ...action.payload,
      };
    },
    
    setSubmissionStep: (state, action: PayloadAction<PatientFeedbackState['submissionStep']>) => {
      state.submissionStep = action.payload;
    },
    
    setFeedbackType: (state, action: PayloadAction<string>) => {
      state.selectedFeedbackType = action.payload;
    },
    
    // File management
    addAttachedFile: (state, action: PayloadAction<File>) => {
      state.attachedFiles.push(action.payload);
    },
    
    removeAttachedFile: (state, action: PayloadAction<number>) => {
      state.attachedFiles.splice(action.payload, 1);
    },
    
    setVoiceRecording: (state, action: PayloadAction<Blob | null>) => {
      state.voiceRecording = action.payload;
    },
    
    // UI state management
    clearSubmissionError: (state) => {
      state.submissionError = null;
    },
    
    clearTrackingError: (state) => {
      state.trackingError = null;
    },
    
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    
    // Reset form
    resetSubmission: (state) => {
      state.currentSubmission = null;
      state.submissionStep = 'content';
      state.selectedFeedbackType = 'general';
      state.attachedFiles = [];
      state.voiceRecording = null;
      state.submissionError = null;
      state.successMessage = null;
    },
    
    // Real-time updates (from WebSocket)
    updateFeedbackStatus: (state, action: PayloadAction<{ id: number; status: string; updates?: Partial<PatientFeedback> }>) => {
      const { id, status, updates } = action.payload;
      if (state.trackedFeedback[id]) {
        state.trackedFeedback[id] = {
          ...state.trackedFeedback[id],
          status: status as PatientFeedback['status'],
          ...updates,
        };
      }
    },
    
    addNewResponse: (state, action: PayloadAction<{ feedbackId: number; response: StaffResponse }>) => {
      const { feedbackId, response } = action.payload;
      if (state.trackedFeedback[feedbackId]) {
        if (!state.trackedFeedback[feedbackId].responses) {
          state.trackedFeedback[feedbackId].responses = [];
        }
        state.trackedFeedback[feedbackId].responses!.push(response);
      }
    },
  },
  
  extraReducers: (builder) => {
    // Submit feedback
    builder
      .addCase(submitFeedback.pending, (state) => {
        state.isSubmitting = true;
        state.submissionError = null;
      })
      .addCase(submitFeedback.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.submittedFeedback.push(action.payload);
        state.submissionStep = 'submitted';
        state.successMessage = action.payload.message;
        state.totalSubmissions += 1;
        if (action.payload.status !== 'resolved' && action.payload.status !== 'closed') {
          state.pendingFeedback += 1;
        }
      })
      .addCase(submitFeedback.rejected, (state, action) => {
        state.isSubmitting = false;
        const error = action.payload as any;
        state.submissionError = typeof error === 'string' ? error : error?.message || 'Submission failed';
      })
      
      // Track feedback
      .addCase(trackFeedback.pending, (state) => {
        state.isTracking = true;
        state.trackingError = null;
      })
      .addCase(trackFeedback.fulfilled, (state, action) => {
        state.isTracking = false;
        state.trackedFeedback[action.payload.id!] = action.payload;
      })
      .addCase(trackFeedback.rejected, (state, action) => {
        state.isTracking = false;
        state.trackingError = action.payload as string || 'Tracking failed';
      })
      
      // Upload file
      .addCase(uploadFile.pending, (state) => {
        state.isUploadingFile = true;
        state.uploadError = null;
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        state.isUploadingFile = false;
        // Handle successful file upload
        if (action.payload.feedback_id) {
          const feedbackId = action.payload.feedback_id;
          if (state.trackedFeedback[feedbackId]) {
            if (!state.trackedFeedback[feedbackId].files) {
              state.trackedFeedback[feedbackId].files = [];
            }
            // File info would be added via separate API call
          }
        }
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.isUploadingFile = false;
        state.uploadError = action.payload as string || 'File upload failed';
      })
      
      // Rate satisfaction
      .addCase(rateSatisfaction.pending, (state) => {
        state.isRatingSubmitted = false;
      })
      .addCase(rateSatisfaction.fulfilled, (state, action) => {
        state.isRatingSubmitted = true;
        state.successMessage = 'Thank you for your feedback rating!';
      })
      .addCase(rateSatisfaction.rejected, (state, action) => {
        state.submissionError = action.payload as string || 'Rating submission failed';
      })
      
      // Fetch user feedback
      .addCase(fetchUserFeedback.fulfilled, (state, action) => {
        state.feedbackHistory = action.payload;
        state.totalSubmissions = action.payload.length;
        state.pendingFeedback = action.payload.filter(
          (f: PatientFeedback) => f.status !== 'resolved' && f.status !== 'closed'
        ).length;
      });
  },
});

export const {
  updateSubmission,
  setSubmissionStep,
  setFeedbackType,
  addAttachedFile,
  removeAttachedFile,
  setVoiceRecording,
  clearSubmissionError,
  clearTrackingError,
  clearSuccessMessage,
  resetSubmission,
  updateFeedbackStatus,
  addNewResponse,
} = patientFeedbackSlice.actions;

export default patientFeedbackSlice.reducer;